import type { UseTuningSession } from '../hooks/useTuningSession';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, InfoCard } from '../components/Cards';
import { CheckCircle2 } from 'lucide-react';
import clsx from 'clsx';

interface Props { tuning: UseTuningSession }

const LIMB_PROS = [
  'Simple install — tie to limb, no press needed',
  'Stays UP longer in shot cycle (more arrow guidance)',
  'Fail-safe: cord break = rest stays UP, arrow still flies',
  'No press needed for timing adjustments',
  'Holds tune well — limbs don\'t stretch',
];
const LIMB_CONS = [
  'Slightly noisier (launcher snaps fast)',
  'Fewer micro-adjustment options vs cable rests',
];
const CABLE_PROS = [
  'Quieter, smoother launcher descent',
  'More micro-adjustment options for precision tuning',
  'Preferred by target archers + indoor leagues',
];
const CABLE_CONS = [
  'Requires bow press to reposition cord clamp',
  'Cables stretch → need re-timing each season',
  'Cord break = rest stays DOWN (lost arrow)',
  'Steeper learning curve',
];

/**
 * Step 6 — Rest Type Branch
 *
 * User picks limb-driven or cable-driven. The choice unlocks step 6a or 6b
 * in the sidebar and hides the other branch. Selection can be changed at
 * any time (the other branch's step data is preserved).
 */
export function RestBranchStep({ tuning }: Props) {
  const restType = tuning.session.branches.restType;
  return (
    <>
      <GoalCard title="Pick the rest you're running">
        The two rest families time differently — one tied to the limb, one tied to
        the cable. The wizard's next step adapts to what you pick. You can change
        this later without losing your other data.
      </GoalCard>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">Which rest style is on your bow?</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <RestCard
            label="Limb-Driven"
            tagline="Tied to the limb. Simpler, more forgiving, fail-safe."
            examples="QAD HDX (limb mode), Vapor Trail Gen 7, Hamskea Hybrid Pro"
            pros={LIMB_PROS}
            cons={LIMB_CONS}
            selected={restType === 'limb_driven'}
            onClick={() => tuning.setRestType('limb_driven')}
          />
          <RestCard
            label="Cable-Driven"
            tagline="Tied to the buss cable. Smoother, more adjustable."
            examples="Hamskea Trinity Hunter Pro, RipCord Code Red, Trophy Taker SS"
            pros={CABLE_PROS}
            cons={CABLE_CONS}
            selected={restType === 'cable_driven'}
            onClick={() => tuning.setRestType('cable_driven')}
          />
        </div>
      </Card>

      {restType && (
        <TipCard
          title={restType === 'limb_driven' ? 'Limb-driven path — what\'s next' : 'Cable-driven path — what\'s next'}
          defaultOpen
        >
          <p className="mb-2">
            The next step walks you through {restType === 'limb_driven'
              ? 'tying the actuation cord to your bottom limb, positioning the cord clamp toward limb tip vs limb pocket to control timing, and verifying the launcher supports the arrow for 50-70% of the power stroke.'
              : 'clamping the activation cord onto the down-traveling buss cable, positioning the clamp on the cable to control when the rest reaches full-up, and verifying the launcher supports the arrow for 50-70% of the power stroke.'}
          </p>
        </TipCard>
      )}

      <InfoCard title="Not sure which one you have?">
        <p className="mb-2">
          Look at your rest's actuation cord (the string coming off the launcher arm).
          If it goes to a <strong>clamp on your limb</strong> → limb-driven. If it goes
          to a <strong>clamp on a cable</strong> → cable-driven.
        </p>
        <p>
          Most "drop-away" rests sold today can be set up either way — the rest itself
          isn't inherently one or the other. Pick the mode your rest is currently in,
          or the mode you want to set it up in.
        </p>
      </InfoCard>
    </>
  );
}

function RestCard({
  label, tagline, examples, pros, cons, selected, onClick,
}: {
  label: string; tagline: string; examples: string;
  pros: string[]; cons: string[];
  selected: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'text-left rounded-lg border p-4 transition',
        selected
          ? 'border-ember-500 bg-ember-500/10'
          : 'border-zinc-800 bg-zinc-950 hover:border-zinc-700',
      )}
    >
      <div className="flex items-center gap-2 mb-1">
        <p className={clsx('font-semibold', selected ? 'text-ember-500' : 'text-zinc-100')}>
          {label}
        </p>
        {selected && <CheckCircle2 size={14} className="text-ember-500" />}
      </div>
      <p className="text-xs text-zinc-400 mb-2 leading-relaxed">{tagline}</p>
      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">
        e.g. {examples}
      </p>
      <div className="border-t border-zinc-800/60 pt-2 space-y-2">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 mb-1">Pros</p>
          <ul className="text-[11px] text-zinc-300 space-y-0.5">
            {pros.map((p, i) => <li key={i}>• {p}</li>)}
          </ul>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-rose-400 mb-1">Cons</p>
          <ul className="text-[11px] text-zinc-300 space-y-0.5">
            {cons.map((c, i) => <li key={i}>• {c}</li>)}
          </ul>
        </div>
      </div>
    </button>
  );
}
